import { useEffect } from 'react';
import { dispatchAction } from '@ardoq/rxbeach';
import { ViewIds } from '@ardoq/api-types';
import {
  TimelineRenderingLayer,
  TimelineRenderingLayerStaticProps,
  TimelineRenderingLayerViewSettingsProps,
  TimelineViewCommands,
  ViewModelSourceData,
} from '@ardoq/timeline2024';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import type {
  HasDataWorkToken,
  ViewStatusReporter,
} from 'tabview/activeViewsStatus/types';
import { getTimelineViewComponentIds } from './viewComponentIds';

export type ViewpointModeTimelineSourceDataLayerProps =
  TimelineRenderingLayerStaticProps &
    TimelineRenderingLayerViewSettingsProps &
    HasDataWorkToken & {
      commands: TimelineViewCommands;
      sourceData: ViewModelSourceData;
      viewStatusReporter: ViewStatusReporter;
    };

const publishEmptyStateData = (isViewEmpty: boolean) =>
  dispatchAction(
    updateViewEmptyStateData({
      viewEmptyStateData: { isViewEmpty },
      viewId: ViewIds.TIMELINE,
    })
  );

const publishViewComponentIds = (
  renderedComponents: Parameters<typeof getTimelineViewComponentIds>[0]
) =>
  dispatchAction(
    readViewComponentIds({
      [ViewIds.TIMELINE]: getTimelineViewComponentIds(renderedComponents),
    })
  );

const ViewpointModeTimelineSourceDataLayer = ({
  viewInstanceId,
  settingsControlsHandlers,
  commands,
  ViewLegendComponent,
  EmptyStateComponent,
  uiOnlyViewSettings,
  sourceData,
  dataWorkToken,
  viewStatusReporter,
}: ViewpointModeTimelineSourceDataLayerProps) => {
  const {
    isViewEmpty,
    fieldsAndItemsSourceData,
    viewRelevantComponentsScopeData,
  } = sourceData;

  useEffect(() => {
    publishEmptyStateData(isViewEmpty);
  }, [isViewEmpty]);

  useEffect(() => {
    publishViewComponentIds({
      fieldsAndItemsSourceData,
      viewRelevantComponentsScopeData,
    });
  }, [fieldsAndItemsSourceData, viewRelevantComponentsScopeData]);

  useEffect(() => {
    viewStatusReporter.reportWorkFinished(dataWorkToken);
  }, [viewStatusReporter, dataWorkToken]);

  return (
    <TimelineRenderingLayer
      viewInstanceId={viewInstanceId}
      settingsControlsHandlers={settingsControlsHandlers}
      commands={commands}
      ViewLegendComponent={ViewLegendComponent}
      EmptyStateComponent={EmptyStateComponent}
      sourceData={sourceData}
      uiOnlyViewSettings={uiOnlyViewSettings}
    />
  );
};

export { ViewpointModeTimelineSourceDataLayer };
